import { useCallback, useEffect, useRef, useState } from 'react';
import { isAxiosError } from 'axios';
import { connectLiveTranscript } from '@utils/api/realtime/live-transcript-connection.ts';
import { saveLiveCorrections } from '@utils/api/realtime/live-transcript.ts';
import { loadLiveRange } from '@utils/api/realtime/live-transcript-range.ts';
import { TokenExtension } from '@utils/extensions/token.extension.ts';
import {
  acceptLiveSave,
  LiveDraft,
  mergeLiveSegments
} from '@utils/editors/live-transcript-state.ts';
import { useLiveSpeakerAutosave } from './use-live-speaker-autosave.ts';
import { useLiveTranscriptEditor } from './use-live-transcript-editor.ts';
import { useLiveTranscriptDiscard } from './use-live-transcript-discard.ts';
import { useLiveTranscriptUnsavedWarning } from './use-live-transcript-unsaved-warning.ts';
import { useLiveTranscriptStop } from './use-live-transcript-stop.ts';
import { LiveSessionStatus } from '@utils/types/editors/live-transcript.ts';

type ErrorState = 'load' | 'save' | 'conflict' | 'connection' | null;
export const useLiveTranscript = (sessionId: string) => {
  const identity = TokenExtension.get().decoded;
  const scope = `${identity?.oid}:${identity?.uid}:${sessionId}`;
  const draftKey = `live-transcript-draft:${scope}`;
  const [rows, setRows] = useState<LiveDraft[]>([]);
  const rowsRef = useRef<LiveDraft[]>([]);
  const [documentId, setDocumentId] = useState<string | null>(null);
  const [canEdit, setCanEdit] = useState(false);
  const [partial, setPartial] = useState('');
  const [connected, setConnected] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [draftStorageFailed, setDraftStorageFailed] = useState(false);
  const [error, setError] = useState<ErrorState>(null);
  const [owner, setOwner] = useState<LiveSessionStatus | null>(null);
  const cursor = useRef(0),
    generation = useRef(0);
  const hydratedKey = useRef<string | null>(null);
  const refreshRef = useRef<() => Promise<void>>(async () => {});
  const savingRef = useRef<number | null>(null);
  const discarded = useRef(false);
  const retryMode = useRef<'all' | 'speaker' | null>(null);
  const commit = useCallback(
    (update: (current: LiveDraft[]) => LiveDraft[]) => {
      const next = update(rowsRef.current);
      rowsRef.current = next;
      setRows(next);
    },
    []
  );
  useEffect(() => {
    if (
      discarded.current ||
      hydratedKey.current !== draftKey ||
      rows !== rowsRef.current
    )
      return;
    try {
      const dirty = rows.filter(row => row.dirty);
      if (dirty.length) localStorage.setItem(draftKey, JSON.stringify(dirty));
      else localStorage.removeItem(draftKey);
      setDraftStorageFailed(false);
    } catch {
      setDraftStorageFailed(rows.some(row => row.dirty));
    }
  }, [draftKey, rows]);
  useEffect(() => {
    ++generation.current;
    cursor.current = 0;
    setDocumentId(null);
    setCanEdit(false);
    setPartial('');
    setLoading(true);
    setError(null);
    setOwner(null);
    setSaving(false);
    setDraftStorageFailed(false);
    savingRef.current = null;
    hydratedKey.current = null;
    discarded.current = false;
    commit(() => []);
    try {
      const recovered: unknown = JSON.parse(
        localStorage.getItem(draftKey) || '[]'
      );
      if (Array.isArray(recovered)) {
        commit(() =>
          recovered
            .filter((row): row is LiveDraft =>
              Boolean(
                row &&
                  typeof row === 'object' &&
                  row.dirty === true &&
                  typeof row.editVersion === 'number' &&
                  row.segment &&
                  typeof row.segment.segment_id === 'string' &&
                  typeof row.segment.sequence === 'number' &&
                  typeof row.segment.revision === 'number' &&
                  Array.isArray(row.segment.utterance?.children)
              )
            )
            .map(row => ({ ...row, textDirty: row.textDirty ?? true }))
        );
      }
    } catch {
      /* Ignore malformed recovery; the server remains authoritative. */
    }
    hydratedKey.current = draftKey;
    const connection = connectLiveTranscript({
      sessionId,
      cursor,
      onSnapshot: page =>
        commit(previous => mergeLiveSegments(previous, page.segments)),
      setDocumentId,
      setCanEdit,
      setPartial,
      setConnected,
      setLoading,
      setError,
      setOwner
    });
    refreshRef.current = connection.refresh;
    return () => {
      ++generation.current;
      connection.dispose();
    };
  }, [commit, draftKey, sessionId]);
  const speakerSaveFailed = useCallback(() => {
    retryMode.current = 'speaker';
  }, []);
  const speakerAutosave = useLiveSpeakerAutosave({
    sessionId,
    scope,
    canEdit,
    generation,
    rows: rowsRef,
    saving: savingRef,
    commit,
    setSaving,
    setError,
    onFailure: speakerSaveFailed
  });
  const { edit, editSpeaker } = useLiveTranscriptEditor({
    canEdit,
    commit,
    scheduleSpeakerSave: speakerAutosave.schedule
  });
  const save = useCallback(
    async (segmentIds?: string[]): Promise<boolean> => {
      if (!canEdit || savingRef.current !== null) return false;
      speakerAutosave.cancel(segmentIds);
      const selected = segmentIds ? new Set(segmentIds) : null;
      const inRange = (row: LiveDraft) =>
        !selected || selected.has(row.segment.segment_id);
      const dirty = rowsRef.current.filter(row => row.dirty && inRange(row));
      if (!dirty.length) return true;
      const active = generation.current;
      savingRef.current = active;
      setSaving(true);
      speakerAutosave.clearError();
      setError(null);
      try {
        for (let offset = 0; offset < dirty.length; offset += 500) {
          const batch = dirty.slice(offset, offset + 500);
          const sent = new Map(
            batch.map(row => [row.segment.segment_id, row.editVersion])
          );
          const saved = await saveLiveCorrections(
            sessionId,
            batch.map(row => ({
              segment_id: row.segment.segment_id,
              revision: row.segment.revision,
              utterance: row.segment.utterance
            }))
          );
          if (generation.current !== active) return false;
          commit(previous => acceptLiveSave(previous, sent, saved.updated));
          speakerAutosave.acknowledge([...sent.keys()]);
        }
        return !rowsRef.current.some(row => row.dirty && inRange(row));
      } catch (failure) {
        if (generation.current === active) {
          retryMode.current = 'all';
          setError(
            isAxiosError(failure) && failure.response?.status === 409
              ? 'conflict'
              : 'save'
          );
        }
        return false;
      } finally {
        if (savingRef.current === active) savingRef.current = null;
        if (generation.current === active) setSaving(false);
      }
    },
    [canEdit, commit, sessionId, speakerAutosave]
  );
  const reloadSaved = useCallback(async () => {
    if (savingRef.current !== null) return;
    const active = generation.current;
    const versions = new Map(
      rowsRef.current.map(row => [row.segment.segment_id, row.editVersion])
    );
    try {
      const loaded = await loadLiveRange(sessionId);
      if (generation.current !== active) return;
      const saved: LiveDraft[] = loaded.segments.map(segment => ({
        segment,
        dirty: false,
        editVersion: 0
      }));
      const after = loaded.cursor;
      commit(previous => {
        const known = new Map(
          previous.map(row => [row.segment.segment_id, row])
        );
        const restored = saved.map(row => {
          const current = known.get(row.segment.segment_id);
          if (
            current?.dirty &&
            current.editVersion !== versions.get(row.segment.segment_id)
          )
            return current;
          return {
            ...row,
            editVersion: (current?.editVersion ?? 0) + 1,
            editorEpoch: (current?.editorEpoch ?? 0) + 1
          };
        });
        return [
          ...restored,
          ...previous.filter(row => row.segment.sequence > after)
        ];
      });
      cursor.current = Math.max(cursor.current, after);
      setError(null);
    } catch {
      if (generation.current === active) setError('load');
    }
  }, [commit, sessionId]);
  const refreshRange = useCallback(
    async (first: number, last: number) => {
      const active = generation.current;
      try {
        const { segments } = await loadLiveRange(sessionId, first, last);
        if (generation.current !== active) return false;
        commit(previous => mergeLiveSegments(previous, segments));
        return true;
      } catch {
        if (generation.current === active) setError('load');
        return false;
      }
    },
    [commit, sessionId]
  );
  const stop = useLiveTranscriptStop({
    sessionId,
    generation,
    setOwner,
    setError
  });
  const discard = useLiveTranscriptDiscard({
    draftKey,
    generation,
    discarded,
    rows: rowsRef,
    saving: savingRef,
    cancelSpeakerAutosave: speakerAutosave.cancel
  });
  useLiveTranscriptUnsavedWarning(rowsRef);
  const hydrated = hydratedKey.current === draftKey;
  return {
    rows: hydrated ? rows : [],
    documentId: hydrated ? documentId : null,
    canEdit: hydrated && canEdit,
    partial: hydrated ? partial : '',
    connected: hydrated && connected,
    loading: !hydrated || loading,
    saving,
    draftStorageFailed,
    error,
    owner,
    scope,
    edit,
    editSpeaker,
    save,
    stop,
    reloadSaved,
    refreshRange,
    retry: () => refreshRef.current(),
    retrySave: () =>
      retryMode.current === 'speaker' ? speakerAutosave.retry() : save(),
    dirty: rows.some(row => row.dirty),
    discard
  };
};
